import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { StatusBadge } from "../components/StatusBadge";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatDate, daysUntil } from "../lib/format";
import { toast } from "sonner";
import { Clock, MapPin, Eye, AlertTriangle, Paperclip } from "lucide-react";
import { SERVICE_STATUS_LABEL, TASK_STATUSES, TASK_STATUS_LABEL, TASK_PRIORITY_LABEL, ROLE_LABEL, divisionById } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

export default function Workspace() {
  const { user, viewAs } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true); setError(null);
    return api.get("/my/workspace")
      .then(({ data }) => { setData(data); setLoading(false); })
      .catch((e) => {
        // Preview backend-mismatch (Owner tidak berdivisi) atau error lain — render empty state
        setData(null);
        setError({
          status: e?.response?.status,
          detail: e?.response?.data?.detail || e?.message || "Gagal memuat ruang kerja",
          previewMismatch: !!e?.__preview_backend_mismatch,
        });
        setLoading(false);
      });
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-sm text-[#8C9096]">Memuat ruang kerja...</div>;

  // Error / empty state (khusus Preview: kasih pesan jelas)
  if (error) {
    if (error.previewMismatch && viewAs) {
      const divLabel = viewAs.division_id ? (divisionById(viewAs.division_id)?.name || viewAs.division_id) : "(tanpa divisi)";
      return (
        <div className="max-w-2xl mx-auto py-10" data-testid="workspace-preview-empty">
          <Card className="p-6 sm:p-8 border border-[#E5E0D8] bg-white shadow-none text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-[#FAF6EE] flex items-center justify-center mx-auto">
              <Eye className="w-6 h-6 text-brand" />
            </div>
            <h2 className="font-display text-lg font-bold text-[#1E2022]">Preview peran: {ROLE_LABEL[viewAs.role] || viewAs.role} · {divLabel}</h2>
            <p className="text-sm text-[#5C6066]">
              Ruang Kerja divisi menampilkan tugas & acara <b>berdasarkan divisi user yang login</b>. Saat Preview, backend tetap mengenali Anda sebagai <b>Owner (tidak berdivisi)</b> — jadi data spesifik divisi tidak tersedia. Ini <b>bukan error</b>, hanya karena Preview bersifat read-only dan tidak mengubah token autentikasi.
            </p>
            <div className="text-[11px] text-[#8C9096] italic pt-1">
              Untuk melihat contoh data divisi, buka menu <b>Semua Divisi</b>, <b>Daftar Booking</b>, atau <b>Tugas & Progres</b> — data itu tersedia lintas divisi untuk Owner.
            </div>
            <div className="text-[10px] text-[#B7B1A5] pt-2">Kode error: HTTP {error.status || "??"}</div>
          </Card>
        </div>
      );
    }
    return (
      <div className="max-w-xl mx-auto py-10" data-testid="workspace-error">
        <Card className="p-6 border border-rose-200 bg-rose-50 text-center space-y-2">
          <AlertTriangle className="w-6 h-6 text-rose-500 mx-auto"/>
          <div className="text-sm font-semibold text-rose-700">Tidak dapat memuat ruang kerja</div>
          <div className="text-xs text-[#5C6066]">{error.detail}</div>
        </Card>
      </div>
    );
  }

  if (!data) return <div className="text-sm text-[#8C9096]">Memuat ruang kerja...</div>;

  const changeTaskStatus = async (id, status) => {
    try {
      await api.put(`/tasks/${id}`, { status });
      toast.success("Status diperbarui"); load();
    } catch (e) { if (!e?.__preview_blocked) toast.error("Gagal update status"); }
  };
  const changeServiceStatus = async (bid, sid, status) => {
    try {
      await api.put(`/bookings/${bid}/services/${sid}`, { status });
      toast.success("Progres divisi diperbarui"); load();
    } catch (e) { if (!e?.__preview_blocked) toast.error("Gagal update progres"); }
  };

  return (
    <div className="space-y-6" data-testid="workspace-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Ruang Kerja {data.division?.short}</h1>
        <p className="text-sm text-[#5C6066] mt-1">{data.division?.name}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="ws-sections-summary">
        <KpiSmall label="Terlambat" value={data.tasks_overdue.length} tone="danger" testId="ws-tasks-overdue" />
        <KpiSmall label="Hari Ini" value={data.tasks_today.length} tone="warn" testId="ws-tasks-today" />
        <KpiSmall label="Mendatang" value={data.tasks_upcoming.length} tone="info" testId="ws-tasks-upcoming" />
        <KpiSmall label="Acara Aktif" value={data.bookings.length} tone="normal" testId="ws-active-events" />
      </div>

      {/* Sections: Tersedia (claim) → Terlambat → Hari Ini → Mendatang → Selesai */}
      <div className="space-y-4">
        {(() => {
          const isAvail = (t) => t.status === "Available";
          const availTasks = [...(data.tasks_overdue || []), ...(data.tasks_today || []), ...(data.tasks_upcoming || [])].filter(isAvail);
          const remOverdue = (data.tasks_overdue || []).filter((t) => !isAvail(t));
          const remToday = (data.tasks_today || []).filter((t) => !isAvail(t));
          const remUpcoming = (data.tasks_upcoming || []).filter((t) => !isAvail(t));
          return (
            <>
              <TaskSection title="Tersedia untuk Divisi Saya" tone="gold" tasks={availTasks} emptyMsg="Belum ada tugas tersedia untuk diambil." changeStatus={changeTaskStatus} reload={load} />
              <TaskSection title="Terlambat" tone="danger" tasks={remOverdue} emptyMsg="Tidak ada tugas terlambat." changeStatus={changeTaskStatus} reload={load} />
              <TaskSection title="Hari Ini" tone="warn" tasks={remToday} emptyMsg="Tidak ada tugas hari ini." changeStatus={changeTaskStatus} reload={load} />
              <TaskSection title="Mendatang" tone="info" tasks={remUpcoming} emptyMsg="Belum ada tugas mendatang." changeStatus={changeTaskStatus} reload={load} />
              <TaskSection title="Selesai (7 hari terakhir)" tone="muted" tasks={data.tasks_done_recent || []} emptyMsg="Belum ada yang selesai dalam 7 hari." changeStatus={changeTaskStatus} reload={load} hideActions />
            </>
          );
        })()}
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none">
        <div className="px-5 py-4 border-b border-[#E5E0D8]"><h2 className="font-display font-semibold">Acara Divisi Saya</h2></div>
        <div className="divide-y divide-[#F0EBE0] max-h-[500px] overflow-y-auto">
          {data.bookings.length === 0 && <div className="p-8 text-sm text-center text-[#8C9096]">Belum ada acara.</div>}
          {data.bookings.map((b) => {
            const d = daysUntil(b.event_date);
            return (
              <div key={b.booking_id} className="p-4">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <Link to={`/bookings/${b.booking_id}`} className="text-sm font-medium hover:text-brand" data-testid={`ws-booking-${b.booking_code}`}>
                      {b.client_name}
                    </Link>
                    <div className="text-[11px] text-[#8C9096] font-mono">{b.booking_code}</div>
                  </div>
                  <span className="text-xs text-brand font-semibold">{d >= 0 ? `H-${d}` : `H+${Math.abs(d)}`}</span>
                </div>
                <div className="mt-2 text-xs text-[#5C6066] flex items-center gap-1.5"><MapPin className="w-3 h-3" />{b.venue} • {b.location}</div>
                <div className="mt-1 text-xs text-[#5C6066] flex items-center gap-1.5"><Clock className="w-3 h-3" />{formatDate(b.event_date)} {b.start_time}</div>
                {b.service && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-[#5C6066] mb-1">
                      <span>Order: {b.service.order_details || "-"}</span>
                      <StatusBadge status={b.service.status} label={SERVICE_STATUS_LABEL[b.service.status]} />
                    </div>
                    <Progress value={b.service.progress || 0} className="h-1.5 mt-1" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// Compact task section with per-card inline actions (mobile-first)
function TaskSection({ title, tone, tasks, emptyMsg, changeStatus, reload, hideActions }) {
  const [busy, setBusy] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  const toneBar = tone === "danger" ? "bg-rose-500" : tone === "warn" ? "bg-amber-500" : tone === "info" ? "bg-sky-500" : tone === "gold" ? "bg-[#B7A175]" : "bg-[#C4A57B]";
  const start = async (t) => { setBusy(t.id); try { await api.put(`/tasks/${t.id}`, { status: "In Progress" }); toast.success("Tugas dimulai"); reload(); } catch(e){ if(!e?.__preview_blocked) toast.error("Gagal"); } finally{ setBusy(null); } };
  const claim = async (t) => { setBusy(t.id); try { await api.post(`/tasks/${t.id}/claim`, { note: "" }); toast.success("Tugas diambil"); reload(); } catch(e){ if(!e?.__preview_blocked) toast.error(e?.response?.data?.detail || "Gagal ambil"); } finally{ setBusy(null); } };
  const submit = async (t) => {
    const note = window.prompt("Catatan penyelesaian (opsional):", "") || "";
    setBusy(t.id);
    try {
      await api.post(`/tasks/${t.id}/submit-done`, { note });
      toast.success("Diklaim selesai — menunggu verifikasi Manager");
      reload();
    } catch(e){ if(!e?.__preview_blocked) toast.error("Gagal"); }
    finally{ setBusy(null); }
  };
  const block = async (t) => { const note = window.prompt("Apa kendalanya?"); if (!note) return; setBusy(t.id); try { await api.post(`/tasks/${t.id}/block`, { note }); toast.success("Kendala dilaporkan ke Manager"); reload(); } catch(e){ if(!e?.__preview_blocked) toast.error("Gagal"); } finally{ setBusy(null); } };
  const openUpload = (t) => {
    if (!t.booking_id) { toast.error("Task tanpa booking — lampiran tidak tersedia"); return; }
    uploadTargetRef.current = t;
    fileInputRef.current?.click();
  };
  const onFileSelected = async (e) => {
    const t = uploadTargetRef.current;
    const file = e.target.files?.[0];
    e.target.value = ""; // reset input so same file can be re-picked
    if (!t || !file) return;
    setBusy(t.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("booking_id", t.booking_id);
      if (t.division_id) fd.append("division_id", t.division_id);
      fd.append("category", "TaskEvidence");
      fd.append("description", `task:${t.id} — ${t.title}`);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Lampiran terkirim");
    } catch(err) {
      if (!err?.__preview_blocked) toast.error(err?.response?.data?.detail || "Gagal upload");
    } finally {
      setBusy(null);
      uploadTargetRef.current = null;
    }
  };
  return (
    <Card className="border border-[#E5E0D8] bg-white shadow-none">
      <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFileSelected} data-testid={`task-upload-input-${title}`} />
      <div className="px-4 sm:px-5 py-3 border-b border-[#E5E0D8] flex items-center gap-2">
        <div className={`w-1.5 h-5 rounded ${toneBar}`}/>
        <h2 className="font-display font-semibold text-sm sm:text-base">{title}</h2>
        <span className="text-[11px] text-[#8C9096] ml-1">({tasks.length})</span>
      </div>
      <div className="divide-y divide-[#F0EBE0]">
        {tasks.length === 0 && <div className="p-6 text-sm text-center text-[#8C9096]">{emptyMsg}</div>}
        {tasks.map((t) => {
          const d = daysUntil(t.due_date);
          return (
            <div key={t.id} className="p-3 sm:p-4" data-testid={`task-row-${t.id}`}>
              <div className="flex items-start gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{t.title}</span>
                    <StatusBadge status={t.priority} label={TASK_PRIORITY_LABEL[t.priority]} />
                    {t.blocked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold">KENDALA</span>}
                    {t.submitted_done && !t.verified_done && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">MENUNGGU VERIFIKASI</span>}
                  </div>
                  <div className="text-[11px] text-[#8C9096] mt-1 truncate">
                    {t.booking_code} • {t.event_name} • {d < 0 ? `Terlambat ${Math.abs(d)} hari` : d === 0 ? "Hari ini" : `Jatuh tempo ${formatDate(t.due_date)}`}
                  </div>
                  {t.blocked && t.blocker_note && (
                    <div className="text-[11px] text-rose-700 mt-1 bg-rose-50 border border-rose-200 px-2 py-1 rounded">
                      <b>Kendala:</b> {t.blocker_note}
                    </div>
                  )}
                </div>
                {!hideActions && (
                  <div className="flex flex-wrap gap-1.5">
                    {t.status === "Available" && <button onClick={() => claim(t)} disabled={busy===t.id} className="h-9 px-3 rounded-md bg-[#B7A175] text-white text-xs font-semibold hover:bg-[#A08A5D] disabled:opacity-60" data-testid={`task-claim-${t.id}`}>Ambil Tugas</button>}
                    {(t.status === "To Do" || t.status === "Claimed") && <button onClick={() => start(t)} disabled={busy===t.id} className="h-9 px-3 rounded-md bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 disabled:opacity-60" data-testid={`task-start-${t.id}`}>Mulai</button>}
                    {t.status !== "Done" && t.status !== "Available" && !t.blocked && !t.submitted_done && <button onClick={() => submit(t)} disabled={busy===t.id} className="h-9 px-3 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60" data-testid={`task-submit-${t.id}`}>Kirim Selesai</button>}
                    {t.status !== "Done" && t.status !== "Available" && !t.blocked && <button onClick={() => block(t)} disabled={busy===t.id} className="h-9 px-3 rounded-md border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-50" data-testid={`task-block-${t.id}`}>Kendala</button>}
                    {t.booking_id && t.status !== "Available" && <button onClick={() => openUpload(t)} disabled={busy===t.id} title="Unggah bukti/lampiran" className="h-9 px-2.5 rounded-md border border-[#E5E0D8] text-[#5C6066] text-xs font-semibold hover:bg-[#FAF6EE] disabled:opacity-60 inline-flex items-center gap-1" data-testid={`task-upload-${t.id}`}><Paperclip className="w-3.5 h-3.5"/>Lampiran</button>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const KpiSmall = ({ label, value, tone, testId }) => (
  <Card className="p-3 sm:p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid={testId}>
    <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[#8C9096] font-semibold">{label}</div>
    <div className={`mt-1 font-display text-2xl font-bold ${tone === "danger" ? "text-rose-700" : tone === "warn" ? "text-amber-700" : tone === "info" ? "text-sky-700" : ""}`}>{value}</div>
  </Card>
);
